import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  inject,
  isDevMode,
  NgModule,
  provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { ButtonComponent } from './components/button/button.component';
import { InputComponent } from './components/input/input.component';
import { ProfileFormComponent } from './components/profile-form/profile-form.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { HelpComponent } from './pages/help/help.component';
import { ProgressComponent } from './pages/progress/progress.component';
import { ResourcesComponent } from './pages/resources/resources.component';

import { DialogModule } from '@angular/cdk/dialog';
import {
  CdkMenu,
  CdkMenuBar,
  CdkMenuItem,
  CdkMenuTrigger,
} from '@angular/cdk/menu';
import { OverlayModule } from '@angular/cdk/overlay';
import { CdkTableModule } from '@angular/cdk/table';
import { NgOptimizedImage } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MatCard,
  MatCardContent,
  MatCardFooter,
  MatCardHeader,
  MatCardImage,
  MatCardSubtitle,
  MatCardTitle,
} from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import {
  ErrorStateMatcher,
  MatNativeDateModule,
  provideNativeDateAdapter,
  ShowOnDirtyErrorStateMatcher,
} from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerModule,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import {
  MatFormField,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatRow,
  MatTable,
  MatTableModule,
} from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { OAuthModule } from 'angular-oauth2-oidc';
import {
  BaseChartDirective,
  provideCharts,
  withDefaultRegisterables,
} from 'ng2-charts';
import { AuthCallbackComponent } from './auth/auth-callback.component';
import { AuthService } from './auth/auth.service';
import { AutocompleteComponent } from './components/atuocomplete/autocomplete.component';
import { AuthLayoutComponent } from './components/auth-layout-component/auth-layout-component.component';
import { AvatarCompanyComponent } from './components/avatar-company/avatar-company.component';
import { CardComponent } from './components/card/card.component';
import { ChipSelectorComponent } from './components/chip-selector/chip-selector.component';
import { DeviceLicenseBatchCardComponent } from './components/device-license-manage-modal/device-license-batch-card/device-license-batch-card.component';
import { DeviceArtifactOverrideSectionComponent } from './components/device-license-manage-modal/device-software-override/device-artifact-override-section/device-artifact-override-section.component';
import { DeviceBundleOverrideSectionComponent } from './components/device-license-manage-modal/device-software-override/device-bundle-override-section/device-bundle-override-section.component';
import { DeviceSoftwareOverrideReviewComponent } from './components/device-license-manage-modal/device-software-override/device-software-override-review/device-software-override-review.component';
import { DeviceSoftwareOverrideComponent } from './components/device-license-manage-modal/device-software-override/device-software-override.component';
import { CompanyGroupFormComponent } from './components/company-group-form/company-group-form.component';
import { CompanyGroupInformationFormComponent } from './components/company-group-information-form/company-group-information-form.component';
import { CompanyResolverComponent } from './components/company-resolver/company-resolver.component';
import { ComponentCredentialsFormComponent } from './components/component-credentials-form/component-credentials-form.component';
import { ComponentCredentialsListComponent } from './components/component-credentials-list/component-credentials-list.component';
import { DashboardTabsComponent } from './components/dashboard-tabs/dashboard-tabs.component';
import { DashboardFlightListComponent } from './components/dashboard/dashboard-flight-list/dashboard-flight-list.component';
import { DashboardServiceDoneListComponent } from './components/dashboard/dashboard-service-done-list/dashboard-service-done-list.component';
import { DashboardServiceDueListComponent } from './components/dashboard/dashboard-service-due-list/dashboard-service-due-list.component';
import { DeviceActivitySectionComponent } from './components/device-activity-section/device-activity-section.component';
import { DeviceCertificatesComponent } from './components/device-certificates/device-certificates.component';
import { DeviceHistoryComponent } from './components/device-history/device-history.component';
import { DeviceLicenseManageModalComponent } from './components/device-license-manage-modal/device-license-manage-modal.component';
import { DeviceLicenseSegmentComponent } from './components/device-license-segment/device-license-segment.component';
import { DeviceSoftwareOverridesSummaryComponent } from './components/device-license-segment/device-software-overrides-summary/device-software-overrides-summary.component';
import { EffectiveSoftwareSelectionComponent } from './components/effective-software-selection/effective-software-selection.component';
import { DeviceTableComponent } from './components/device-table/device-table.component';
import { DeviceCreateModalComponent } from './components/modals/device-create-modal/device-create-modal.component';
import { DroneFlightsHistoryComponent } from './components/drone-flights-history/drone-flights-history.component';
import { DroneMaintenanceLogbookComponent } from './components/drone-maintenance-log/drone-maintenance-logbook/drone-maintenance-logbook.component';
import { DroneModelVectorTableComponent } from './components/drone-model-table/drone-model-table.component';
import { DroneModelAllComponent } from './components/drone-models/drone-model-all/drone-model-all.component';
import { DroneModelScorpionComponent } from './components/drone-models/drone-model-scorpion/drone-model-scorpion.component';
import { DroneModelVectorComponent } from './components/drone-models/drone-model-vector/drone-model-vector.component';
import { FallbackComponent } from './components/fallback/fallback.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { FullscreenLoadingSpinnerComponent } from './components/fullscreen-loading-spinner/fullscreen-loading-spinner.component';
import { GeneralDeviceInfoComponent } from './components/general-device-info/general-device-info.component';
import { HeaderComponent } from './components/header/header.component';
import { InfoboxComponent } from './components/infobox/infobox.component';
import { ApplicationsOptionsInputComponent } from './components/inputs/applications-options-input/applications-options-input.component';
import { CheckboxInputComponent } from './components/inputs/checkbox-input/checkbox-input.component';
import { CompanyInputComponent } from './components/inputs/company-input/company-input.component';
import { CountryOptionsInputComponent } from './components/inputs/country-options-input/country-options-input.component';
import { IndustryOptionsInputComponent } from './components/inputs/industry-options-input/industry-options-input.component';
import { LicensekeyInputComponent } from './components/inputs/licensekey-input/licensekey-input.component';
import { PostalcodeInputComponent } from './components/inputs/postalcode-input/postalcode-input.component';
import { QsInputComponent } from './components/inputs/qs-input/qs-input.component';
import { StateOrProvinceInputComponent } from './components/inputs/state-or-province-input/state-or-province-input.component';
import { LicenseBatchCardComponent } from './components/license-batch-card/license-batch-card.component';
import { LicenseBatchDetailsComponent } from './components/license-batch-details/license-batch-details.component';
import { LicenseBatchSoftwareOverrideModalComponent } from './components/license-batch-details/license-batch-software-override-modal/license-batch-software-override-modal.component';
import { LicenseBatchFormComponent } from './components/license-batch-form/license-batch-form.component';
import { LicenseBatchListComponent } from './components/license-batch-list/license-batch-list.component';
import { LicenseComponentCardComponent } from './components/license-component-card/license-component-card.component';
import { LicenseManageDevicesComponent } from './components/license-manage-devices/license-manage-devices.component';
import { LicenseManageUsersComponent } from './components/license-manage-users/license-manage-users.component';
import { LicenseProductCardComponent } from './components/license-product-card/license-product-card.component';
import { LicenseProductFormComponent } from './components/license-product-form/license-product-form.component';
import { LicenseProductListComponent } from './components/license-product-list/license-product-list.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { MainLayoutComponent } from './components/main-layout-component/main-layout-component.component';
import { ImpersonationComponent } from './components/impersonation/impersonation.component';
import { MembersTableComponent } from './components/members-table/members-table.component';
import { EditDeviceModalComponent } from './components/modals/edit-device-modal/edit-device-modal.component';
import { EditDroneModalComponent } from './components/modals/edit-drone-modal/edit-drone-modal.component';
import { GroupActionsModalComponent } from './components/modals/group-actions-modal/group-actions-modal.component';
import { InformationModalComponent } from './components/modals/information-modal/information-modal.component';
import { InviteMemberModalComponent } from './components/modals/invite-member-modal/invite-member-modal.component';
import { JoinGroupModalComponent } from './components/modals/join-group-modal/join-group-modal.component';
import { PartListTableComponent } from './components/part-list-table/part-list-table.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { QsTableComponent } from './components/qs-table/qs-table.component';
import { ResponseBannerComponent } from './components/response-banner/response-banner.component';
import { TabButtonComponent } from './components/tab-button/tab-button.component';
import { UploadOptionsComponent } from './components/upload-options/upload-options.component';
import { UploadsHistoryComponent } from './components/uploads-history/uploads-history.component';
import { ActionOnOutsideClickDirective } from './directives/action-on-outside-click.directive';
import { TypeAheadFocusDirective } from './directives/type-ahead-focus.directive';
import { ApiTokenInterceptor } from './interceptor/api-token.interceptor';
import { FrozenApiInterceptor } from './interceptor/frozen-api.interceptor';
import { LoadingInterceptor } from './interceptor/loading.interceptor';
import { AllResourcesComponent } from './pages/all-resources/all-resources.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DeviceDetailsComponent } from './pages/device-details/device-details.component';
import { JoinComponent } from './pages/join/join.component';
import { MasterAdminCompaniesComponent } from './pages/master-admin-companies/master-admin-companies.component';
import { MasterAdminCompanyDetailsComponent } from './pages/master-admin-company-details/master-admin-company-details.component';
import { MasterAdminCompanyFormComponent } from './pages/master-admin-company-form/master-admin-company-form.component';
import { MyProfileComponent } from './pages/my-profile/my-profile.component';
import { UploadsComponent } from './pages/uploads/uploads.component';
import { QuantumDatePipe } from './pipes/quantum-date.pipe';
import { TitleCaseReplacePipe } from './pipes/title-case-replace.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { TranslationInitService } from './services/translation-init.service';
import { CompanyEffects } from './stores/company/company.effects';
import { companyReducer } from './stores/company/company.reducer';
import { EquipmentEffects } from './stores/equipment/equipment.effects';
import { equipmentReducer } from './stores/equipment/equipment.reducer';
import { GcpEffects } from './stores/gcp/gcp.effects';
import { groundControlPointReducer } from './stores/gcp/gcp.reducer';
import { NoFlyZoneEffects } from './stores/noflyzone/noflyzone.effects';
import { noFlyZoneReducer } from './stores/noflyzone/noflyzone.reducer';
import { ProfileEffects } from './stores/profile/profile.effects';
import { profileReducer } from './stores/profile/profile.reducer';
import { ProjectEffects } from './stores/project/project.effects';
import { projectReducer } from './stores/project/project.reducer';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    AllResourcesComponent,
    DashboardComponent,
    DashboardFlightListComponent,
    DashboardServiceDoneListComponent,
    DashboardServiceDueListComponent,
    AvatarComponent,
    InputComponent,
    SidenavComponent,
    ResourcesComponent,
    ProgressComponent,
    HelpComponent,
    ProfileFormComponent,
    FullscreenLoadingSpinnerComponent,
    LoadingSpinnerComponent,
    CompanyInputComponent,
    CountryOptionsInputComponent,
    StateOrProvinceInputComponent,
    IndustryOptionsInputComponent,
    ApplicationsOptionsInputComponent,
    HeaderComponent,
    ResponseBannerComponent,
    PostalcodeInputComponent,
    FallbackComponent,
    TypeAheadFocusDirective,
    ActionOnOutsideClickDirective,
    ProgressBarComponent,
    CompanyGroupFormComponent,
    TabButtonComponent,
    CardComponent,
    MembersTableComponent,
    ImpersonationComponent,
    AvatarCompanyComponent,
    InviteMemberModalComponent,
    LicensekeyInputComponent,
    DashboardTabsComponent,
    CompanyResolverComponent,
    ComponentCredentialsListComponent,
    ComponentCredentialsFormComponent,
    InfoboxComponent,
    CompanyGroupInformationFormComponent,
    CheckboxInputComponent,
    JoinGroupModalComponent,
    JoinComponent,
    GroupActionsModalComponent,
    InformationModalComponent,
    QsInputComponent,
    EditDeviceModalComponent,
    EditDroneModalComponent,
    TruncatePipe,
    MyProfileComponent,
    UploadsComponent,
    UploadOptionsComponent,
    DroneModelVectorComponent,
    DroneModelScorpionComponent,
    DroneModelAllComponent,
    DroneModelVectorTableComponent,
    DroneMaintenanceLogbookComponent,
    DroneFlightsHistoryComponent,
    DeviceActivitySectionComponent,
    DeviceCertificatesComponent,
    DeviceHistoryComponent,
    GeneralDeviceInfoComponent,
    UploadsHistoryComponent,
    DeviceLicenseBatchCardComponent,
    DeviceArtifactOverrideSectionComponent,
    DeviceBundleOverrideSectionComponent,
    DeviceSoftwareOverrideReviewComponent,
    DeviceSoftwareOverrideComponent,
    DeviceLicenseManageModalComponent,
    DeviceLicenseSegmentComponent,
    DeviceSoftwareOverridesSummaryComponent,
    EffectiveSoftwareSelectionComponent,
    DeviceCreateModalComponent,
    DeviceTableComponent,
    MasterAdminCompanyFormComponent,
    MasterAdminCompanyDetailsComponent,
    MasterAdminCompaniesComponent,
    PartListTableComponent,
    LicenseBatchCardComponent,
    LicenseBatchListComponent,
    LicenseBatchFormComponent,
    LicenseBatchDetailsComponent,
    LicenseBatchSoftwareOverrideModalComponent,
    LicenseManageDevicesComponent,
    LicenseManageUsersComponent,
    LicenseComponentCardComponent,
    LicenseProductCardComponent,
    LicenseProductListComponent,
    LicenseProductFormComponent,
    AuthCallbackComponent,
    MainLayoutComponent,
    DeviceDetailsComponent,
    AuthLayoutComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    OAuthModule.forRoot(),
    AutocompleteComponent,
    ChipSelectorComponent,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    FileUploadComponent,
    ReactiveFormsModule,
    OverlayModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    DialogModule,
    CdkMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    StoreModule.forRoot(
      {
        profile: profileReducer,
        company: companyReducer,
        equipment: equipmentReducer,
        groundControlPoints: groundControlPointReducer,
        noFlyZones: noFlyZoneReducer,
        projects: projectReducer,
      },
      {},
    ),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),
    EffectsModule.forRoot([
      ProfileEffects,
      CompanyEffects,
      EquipmentEffects,
      GcpEffects,
      NoFlyZoneEffects,
      ProjectEffects,
    ]),
    CdkMenuBar,
    BaseChartDirective,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatCardSubtitle,
    MatCardTitle,
    MatIcon,
    MatCardImage,
    MatGridList,
    MatGridTile,
    MatLabel,
    MatFormField,
    QsTableComponent,
    MatInput,
    MatDatepickerInput,
    MatNativeDateModule,
    MatDatepickerToggle,
    MatDatepicker,
    MatDatepickerModule,
    MatCheckbox,
    MatSelectModule,
    MatButtonModule,
    MatTable,
    MatTableModule, // Table functionality
    CdkTableModule, // CDK Table features
    MatPaginatorModule, // Pagination
    MatIconModule, // Icons for expand/collapse
    MatChipsModule, // Chips for filtering
    MatColumnDef,
    MatCell,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatRow,
    MatTooltipModule,
    MatDivider,
    MatTabsModule,
    MatStepperModule,
    MatDialogModule,
    NgOptimizedImage,
    QuantumDatePipe,
    MatCardFooter,
    MatPaginator,
    TitleCaseReplacePipe,
    ButtonComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    provideAppInitializer(() => {
      const i18n = inject(TranslationInitService);
      return i18n.init(); // Promise<void>
    }),

    // Auth
    provideAppInitializer(async () => {
      const auth = inject(AuthService);
      await auth.init();
    }),
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MatDialogRef, useValue: {} },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ApiTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: FrozenApiInterceptor, multi: true },
    TranslationInitService,
    provideHttpClient(withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    provideNativeDateAdapter(),
  ],
})
export class AppModule {}
